/**
 * LlmCatalogPage — Unified model catalog (ADR-041/042 §3.1).
 *
 * SystemAdmin-only. Lists every `model_catalog` entry with badges showing
 * how many platform providers + BYOP tenants offer each model.
 *
 * Path: /settings/llm-catalog
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useI18n } from '@/i18n';
import { BookOpen, Loader2, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import apiClient from '@/lib/api-client';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { PROVIDER_METADATA } from '@/features/llm-config/types';
import { ModelPriceCell } from '@/features/model-intelligence';

interface CatalogItem {
  id: string;
  slug: string;
  display_name: string;
  publisher: string;
  model_format: string;
  modality: string;
  family: string | null;
  version_label: string | null;
  lifecycle_status: string;
  context_window: number | null;
  supports_vision: boolean;
  supports_tools: boolean;
  icon_url: string | null;
  documentation_url: string | null;
  offerings_by_provider_type: Record<string, number>;
  byop_count: number;
}

interface CatalogResponse {
  items: CatalogItem[];
  total: number;
}

interface PlatformProvider {
  id: string;
  provider_type: string;
}

interface PlatformProvidersResponse {
  items: PlatformProvider[];
  total: number;
}

interface PlatformModel {
  id: string;
  provider_id: string;
  catalog_id?: string | null;
  model_id: string;
  display_name: string;
  publisher: string | null;
  model_format: string;
  lifecycle_status: string | null;
  context_window: number;
  supports_vision: boolean;
  supports_tools: boolean;
  supports_embeddings: boolean;
  supports_audio: boolean;
  supports_image_gen: boolean;
  supports_chat: boolean;
}

interface PlatformModelsResponse {
  items: PlatformModel[];
  total: number;
}

function inferModality(model: PlatformModel): string {
  if (model.supports_embeddings) return 'embedding';
  if (model.supports_audio) return 'audio';
  if (model.supports_image_gen) return 'image';
  if (model.supports_chat) return 'chat';
  return 'chat';
}

/** Estados de ciclo de vida → claves i18n (fuera del mapa, valor recibido). */
const LIFECYCLE_LABEL_KEYS: Record<string, string> = {
  stable: 'admin.llmCatalog.lifecycles.stable',
  preview: 'admin.llmCatalog.lifecycles.preview',
  deprecated: 'admin.llmCatalog.lifecycles.deprecated',
  retired: 'admin.llmCatalog.lifecycles.retired',
};

/** Modalidades → claves i18n (fuera del mapa, valor recibido). */
const MODALITY_LABEL_KEYS: Record<string, string> = {
  chat: 'admin.llmCatalog.modalities.chat',
  embedding: 'admin.llmCatalog.modalities.embedding',
  reranker: 'admin.llmCatalog.modalities.reranker',
  image: 'admin.llmCatalog.modalities.image',
  audio: 'admin.llmCatalog.modalities.audio',
};

function mapPlatformModelsToCatalog(
  models: PlatformModel[],
  providersById: Map<string, string>,
): CatalogResponse {
  const grouped = new Map<string, CatalogItem>();

  models.forEach((model) => {
    const groupingKey = model.catalog_id ?? model.model_id;
    const slug = model.model_id;
    const providerType = providersById.get(model.provider_id) ?? 'unknown';

    const existing = grouped.get(groupingKey);
    if (!existing) {
      grouped.set(groupingKey, {
        id: model.id,
        slug,
        display_name: model.display_name,
        publisher: model.publisher ?? '—',
        model_format: model.model_format,
        modality: inferModality(model),
        family: null,
        version_label: null,
        lifecycle_status: model.lifecycle_status ?? 'stable',
        context_window: model.context_window,
        supports_vision: model.supports_vision,
        supports_tools: model.supports_tools,
        icon_url: null,
        documentation_url: null,
        offerings_by_provider_type: {
          [providerType]: 1,
        },
        byop_count: 0,
      });
      return;
    }

    existing.offerings_by_provider_type[providerType] =
      (existing.offerings_by_provider_type[providerType] ?? 0) + 1;
  });

  const items = Array.from(grouped.values()).sort((a, b) =>
    a.display_name.localeCompare(b.display_name),
  );
  return {
    items,
    total: items.length,
  };
}

function useLlmCatalog(params: {
  lifecycleStatus?: string;
  modality?: string;
}) {
  const search = new URLSearchParams();
  if (params.lifecycleStatus)
    search.set('lifecycle_status', params.lifecycleStatus);
  if (params.modality) search.set('modality', params.modality);
  const qs = search.toString();

  return useQuery({
    queryKey: ['llm-catalog', params],
    queryFn: async () => {
      try {
        const { data } = await apiClient.get<CatalogResponse>(
          `/api/v1/admin/llm-catalog${qs ? `?${qs}` : ''}`,
        );
        return data;
      } catch (error: unknown) {
        const statusCode =
          typeof error === 'object' &&
          error !== null &&
          'response' in error &&
          typeof (error as { response?: { status?: number } }).response?.status === 'number'
            ? (error as { response?: { status?: number } }).response?.status
            : null;

        if (statusCode !== 404) {
          throw error;
        }

        const [providersResponse, modelsResponse] = await Promise.all([
          apiClient.get<PlatformProvidersResponse>('/api/v1/admin/platform-providers'),
          apiClient.get<PlatformModelsResponse>('/api/v1/admin/platform-models', {
            params: { active_only: true, page_size: 100 },
          }),
        ]);

        const providersById = new Map<string, string>();
        providersResponse.data.items.forEach((provider) => {
          providersById.set(provider.id, provider.provider_type);
        });

        const fallbackCatalog = mapPlatformModelsToCatalog(
          modelsResponse.data.items,
          providersById,
        );

        const lifecycleStatus = params.lifecycleStatus?.toLowerCase();
        const modality = params.modality?.toLowerCase();
        const filteredItems = fallbackCatalog.items.filter((item) => {
          const lifecycleMatch =
            !lifecycleStatus || item.lifecycle_status.toLowerCase() === lifecycleStatus;
          const modalityMatch = !modality || item.modality.toLowerCase() === modality;
          return lifecycleMatch && modalityMatch;
        });

        return {
          items: filteredItems,
          total: filteredItems.length,
        };
      }
    },
    staleTime: 60_000,
  });
}

const LIFECYCLE_COLORS: Record<string, string> = {
  stable: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  preview: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  deprecated:
    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  retired: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
};

export function LlmCatalogPage() {
  const { t } = useI18n();
  const [lifecycleFilter, setLifecycleFilter] = useState<string>('');
  const [modalityFilter, setModalityFilter] = useState<string>('');
  const [search, setSearch] = useState('');

  /** Valor técnico → etiqueta i18n; sin mapeo, se muestra el valor recibido. */
  const labelFor = (keys: Record<string, string>, value: string) =>
    keys[value] ? t(keys[value]) : value;

  const { data, isLoading, error } = useLlmCatalog({
    lifecycleStatus: lifecycleFilter || undefined,
    modality: modalityFilter || undefined,
  });

  const filtered =
    data?.items.filter(
      (it) =>
        !search ||
        it.slug.toLowerCase().includes(search.toLowerCase()) ||
        it.display_name.toLowerCase().includes(search.toLowerCase()) ||
        it.publisher.toLowerCase().includes(search.toLowerCase()),
    ) ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <BookOpen className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-2xl font-semibold">{t('admin.llmCatalog.title')}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('admin.llmCatalog.subtitle')}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder={t('admin.llmCatalog.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <select
          value={lifecycleFilter}
          onChange={(e) => setLifecycleFilter(e.target.value)}
          className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
        >
          <option value="">{t('admin.llmCatalog.allLifecycles')}</option>
          <option value="stable">{t('admin.llmCatalog.lifecycles.stable')}</option>
          <option value="preview">{t('admin.llmCatalog.lifecycles.preview')}</option>
          <option value="deprecated">{t('admin.llmCatalog.lifecycles.deprecated')}</option>
          <option value="retired">{t('admin.llmCatalog.lifecycles.retired')}</option>
        </select>
        <select
          value={modalityFilter}
          onChange={(e) => setModalityFilter(e.target.value)}
          className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
        >
          <option value="">{t('admin.llmCatalog.allModalities')}</option>
          <option value="chat">{t('admin.llmCatalog.modalities.chat')}</option>
          <option value="embedding">{t('admin.llmCatalog.modalities.embedding')}</option>
          <option value="reranker">{t('admin.llmCatalog.modalities.reranker')}</option>
          <option value="image">{t('admin.llmCatalog.modalities.image')}</option>
          <option value="audio">{t('admin.llmCatalog.modalities.audio')}</option>
        </select>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      )}

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-300">
          {t('admin.llmCatalog.loadError', {
            message:
              error instanceof Error
                ? error.message
                : t('admin.llmCatalog.unknownError'),
          })}
        </div>
      )}

      {/* Table */}
      {data && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-4 py-3 text-left font-medium">
                  {t('admin.llmCatalog.columns.model')}
                </th>
                <th className="px-4 py-3 text-left font-medium">
                  {t('admin.llmCatalog.columns.publisher')}
                </th>
                <th className="px-4 py-3 text-left font-medium">
                  {t('admin.llmCatalog.columns.modality')}
                </th>
                <th className="px-4 py-3 text-left font-medium">
                  {t('admin.llmCatalog.columns.lifecycle')}
                </th>
                <th className="px-4 py-3 text-left font-medium">
                  {t('admin.llmCatalog.columns.offerings')}
                </th>
                <th className="px-4 py-3 text-left font-medium">
                  {t('admin.llmCatalog.columns.price')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filtered.map((item) => (
                <tr
                  key={item.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-800/50"
                >
                  <td className="px-4 py-3">
                    <Link
                      to={`/model-intel/model/${encodeURIComponent(item.slug)}`}
                      className="font-medium text-blue-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-blue-400"
                    >
                      {item.display_name}
                    </Link>
                    <div className="font-mono text-xs text-gray-500">
                      {item.slug}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div>{item.publisher}</div>
                    <div className="text-xs text-gray-500">
                      {item.model_format}
                    </div>
                  </td>
                  <td className="px-4 py-3 capitalize">
                    {labelFor(MODALITY_LABEL_KEYS, item.modality)}
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      className={
                        LIFECYCLE_COLORS[item.lifecycle_status] ??
                        'bg-gray-100 text-gray-700'
                      }
                    >
                      {labelFor(LIFECYCLE_LABEL_KEYS, item.lifecycle_status)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-1">
                      {Object.entries(item.offerings_by_provider_type).map(
                        ([pt, count]) => {
                          const meta = PROVIDER_METADATA[
                            pt as keyof typeof PROVIDER_METADATA
                          ];
                          return (
                            <Badge
                              key={pt}
                              variant="outline"
                              className="text-xs"
                              title={t('admin.llmCatalog.offeringsCount', { count })}
                            >
                              {meta?.displayName ?? pt} · {count}
                            </Badge>
                          );
                        },
                      )}
                      {item.byop_count > 0 && (
                        <Badge
                          variant="outline"
                          className="text-xs bg-purple-50 dark:bg-purple-900/20"
                        >
                          BYOP · {item.byop_count}
                        </Badge>
                      )}
                      {Object.keys(item.offerings_by_provider_type).length ===
                        0 &&
                        item.byop_count === 0 && (
                          <span className="text-xs text-gray-400">
                            {t('admin.llmCatalog.noActiveOfferings')}
                          </span>
                        )}
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top">
                    {/* Sprint 285 G: precio propio/opción vigente desde model-intel (aditivo) */}
                    <ModelPriceCell modelRef={item.slug} showValidity={false} />
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-12 text-center text-gray-500"
                  >
                    {t('admin.llmCatalog.noResults')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default LlmCatalogPage;
